import React from 'react';

import { Box, Typography } from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import _ from 'lodash';

import { PendingChangesActionType } from 'constants/pendingChanges';

import {
  getAccountingFormConfig,
  getInvestorFormConfig,
  getServicerFormConfig,
  PAYMENT_FORM_CONFIG,
  SECTION_HREF_MAP,
  SHIPPING_FORM_CONFIG,
  UNSECURED_HIDDEN_SECTIONS,
} from './EditDrawer.config';
import {
  navTabCustomStyles,
  navTextContentStyles,
  navTextStyles,
  navTextWrapperStyles,
  pendingChangesCounterStyles,
} from './EditDrawer.styles';

export const SECTION_NAME_MAP = {
  [SECTION_HREF_MAP.INVESTOR]: 'Investor Information',
  [SECTION_HREF_MAP.ACCOUNTING]: 'Accounting Information',
  [SECTION_HREF_MAP.SERVICER]: 'Servicer Information',
  [SECTION_HREF_MAP.MERS]: 'MERS',
  [SECTION_HREF_MAP.SHIPPING]: 'Shipping Information',
  [SECTION_HREF_MAP.PAYMENT]: 'Payment Information',
  [SECTION_HREF_MAP.FINAL_DOCUMENTS]: 'Final Documents',
  [SECTION_HREF_MAP.SHIPPER_MILESTONE]: 'Shipper Milestone',
  [SECTION_HREF_MAP.FLIPPED_UNSALABLE]: 'Flipped / Unsalable',
};

export const DEFAULT_SECTION = SECTION_HREF_MAP.INVESTOR;

export { SECTION_HREF_MAP };

const getVisibleSectionNameMap = (isUnsecured = false) => {
  if (!isUnsecured) {
    return SECTION_NAME_MAP;
  }

  return Object.fromEntries(
    Object.entries(SECTION_NAME_MAP).filter(([value]) => !UNSECURED_HIDDEN_SECTIONS.has(value)),
  );
};

const getFieldNamesFromConfig = config => {
  const fieldNames = [];
  const configObj = _.isFunction(config)
    ? config({ investorsOptions: [], servicingCompaniesOptions: [] })
    : config;

  configObj?.groups?.forEach(group => {
    group?.fields?.forEach(field => {
      if (field.nameInModel) {
        fieldNames.push(field.nameInModel);
      }
    });
  });

  return fieldNames;
};

const SECTION_FIELD_NAMES_MAP = {
  [SECTION_HREF_MAP.INVESTOR]: getFieldNamesFromConfig(getInvestorFormConfig),
  [SECTION_HREF_MAP.ACCOUNTING]: getFieldNamesFromConfig(getAccountingFormConfig),
  [SECTION_HREF_MAP.SERVICER]: getFieldNamesFromConfig(getServicerFormConfig),
  [SECTION_HREF_MAP.SHIPPING]: getFieldNamesFromConfig(SHIPPING_FORM_CONFIG),
  [SECTION_HREF_MAP.PAYMENT]: getFieldNamesFromConfig(PAYMENT_FORM_CONFIG),
};

export const calculateSectionPendingChangesCounts = pendingChangesData => {
  const counts = {};

  Object.entries(SECTION_FIELD_NAMES_MAP).forEach(([sectionKey, fieldNames]) => {
    const fieldNamesSet = new Set(fieldNames);
    const count = Object.keys(pendingChangesData || {}).filter(fieldName =>
      fieldNamesSet.has(fieldName),
    ).length;
    counts[sectionKey] = count;
  });

  return counts;
};

export const getNavigationTabs = ({
  applicationId,
  tab,
  history,
  pendingChangesCounts,
  isUnsecured = false,
}) => {
  const visibleSectionNameMap = getVisibleSectionNameMap(isUnsecured);

  return Object.entries(visibleSectionNameMap).map(([value, tabLabel]) => {
    const pendingChangesCount = pendingChangesCounts?.[value] || 0;

    return {
      value,
      label: (
        <Box sx={navTextWrapperStyles}>
          {!!pendingChangesCount && (
            <PendingChangeWrapper
              isSmall
              className="pending-changes-count-wrapper"
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              sx={pendingChangesCounterStyles}
              textSelector="span"
            >
              <Typography className="pending-changes-count" variant="regular12">
                {pendingChangesCount}
              </Typography>
            </PendingChangeWrapper>
          )}

          <Box sx={navTextContentStyles}>
            <PresenceSectionDot sectionId={value} />
            <Typography
              data-truncated
              className="nav-tab-label truncate"
              sx={navTextStyles}
              variant="regular12"
            >
              {tabLabel}
            </Typography>
          </Box>
        </Box>
      ),
      customStyles: navTabCustomStyles,
      onClick: () => {
        const path = `/applications/${applicationId}/management/${tab}/${value}`;
        history.push(path);
      },
    };
  });
};
